<template>
    <div>
        <v-dialog 
            v-model="expediteDialog" 
            width="35%"
            elevation="8"
            persistent>
            <v-card>
                <v-card-title class="headline primary darken-1 white--text">
                    <span class="mr-3">Confirm:</span><span>Expedite this Project</span>
                </v-card-title>
        
                <v-card-text class="mt-3" v-if="expediteDialog">
                    <div v-if="expediteArgs.expedite">
                        <div class="d-block">The cost for expediting this project is: <strong>{{ expedite_charge_display }}</strong></div>
                        <div class="d-block">Once submitted and accepted, this project will receive <u><i>priority</i></u> scheduling and the new estimated delivery timeline for this project will be <strong>{{ expediteArgs.modinfo.expedited_days_to_complete }} business days</strong>, instead of <strong>{{ expediteArgs.modinfo.days_to_complete }} business days</strong>, after receipt of full information.</div>
                        <div class="d-block mt-4">By clicking the confirm button below you are affirming that we are expediting this project on the client's behalf. The client will receive an email notifying them of this change and subsequent charge.</div>
                    </div>
                    <div v-else>
                        <div class="d-block">Once submitted and accepted, this project will <u><i>not</i></u> receive priority scheduling and the estimated delivery timeline for this project will be <strong>{{ expediteArgs.modinfo.days_to_complete }} business days</strong>, instead of <strong>{{ expediteArgs.modinfo.expedited_days_to_complete }} business days</strong>, after receipt of full information.</div>
                        <div class="d-block mt-4">By clicking the confirm button below you are affirming that we are turning off expedited scheduling for this project on the client's behalf. The client will not receive an email notifying them of this change.</div>
                    </div>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn 
                        class="mr-3" 
                        outlined 
                        color="primary darken-1"
                        @click="expediteDialog = false; expediteArgs.expedite = false">
                        Cancel
                    </v-btn>
                    <v-btn 
                        class="white--text" 
                        color="primary darken-1"
                        @click="expediteProject">
                        Confirm
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- <v-dialog
            v-model="projectDataDialog"
            transition="scale-transition"
            origin="center center"
            fullscreen
            style="z-index: 2000">
            <v-card class="d-flex align-start">
                <div style="width: 100%">
                    <ProjectDetails
                        :user_id="this_userId"
                        :project_id="this_projectId"
                        :client_info="this_clientInfo"
                        :project_statuses="project_statuses"
                        openTab="overview"
                        @closeDetailDialog="closeDetailDialog" />
                </div>
            </v-card>
        </v-dialog> -->

        <div class="mb-3">
            <v-progress-linear 
                :value="projectDetails.complete == 0 ? 0.5 : projectDetails.complete"
                :color="projectDetails.status_progress_color"
                class="d-block pa-0 my-2 mr-0"
                :class="projectDetails.status == 'closed' ? 'white--text' : 'heavy--text'"
                height="26"
                rounded>
                <span class="justify-start font-weight-bold">project {{ projectDetails.complete }}% complete</span>
            </v-progress-linear>

            <div v-if="projectDetails.multi_project" class="d-block my-4 align-center">
                <div class="d-inline info--text label_medium mr-3">
                    Multi Service Project(s): 
                </div>
                <v-tooltip
                    v-for="mod in projectDetails.multi_project_modules"
                    :key="mod.project_id"
                    bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-chip
                            small
                            label
                            outlined
                            clickable
                            :color="mod.status_color"
                            class="multi-project-link-chip"
                            @click.stop="openRelatedProject(mod)"
                            v-bind="attrs"
                            v-on="on"
                            style="padding: .625rem .5rem !important; margin-right: .5rem !important">
                            <span class="font-weight-bold">{{ mod.module_name }}: {{ mod.status_text }}</span>
                        </v-chip>
                    </template>
                    <span>{{ getProjectTitleTooltip(mod) }}</span>
                </v-tooltip>
            </div>
        </div>


        <v-row class="justify-space-between">
             <!-- COLUMN 1 ///////////////////////////////////////// -->
            <v-col cols="3" md="3" sm="3" class="">
                <div class="justify-start">
                    <div class="d-block d-flex info--text label_medium justify-space-between">
                        <div class="d-inline"> 
                            <div class="d-inline mr-2">Status:</div>
                            <div class="d-inline font-weight-bold" :class="projectDetails.status_text_color">
                                {{ projectDetails.status_internal_label }}
                            </div>
                        </div>
                        <div class="d-inline justify-end">
                            <v-btn
                                small
                                class="mx-2 elevation-1 mt-n2"
                                fab
                                color="primary darken-1"
                                @click="openStatusMenu( $event )">
                                <v-icon>mdi-palette-swatch-variant</v-icon>
                            </v-btn>

                            <v-menu
                                v-model="isHoldMenuVisible"
                                :position-x="holdMenuPosition.x"
                                :position-y="holdMenuPosition.y"
                                absolute
                                offset-y
                            >
                                <v-list>
                                    <v-list-item
                                        v-for="(option, index) in statusOptions"
                                        :key="index"
                                        :class="option.status_menu_color"
                                        ripple
                                        @click="changeStatus( option.value )"
                                    >
                                        <v-list-item-title 
                                            :style="`color: ${['closed','cloned'].includes(option.value) ? 'lightgray' : 'inherit'}; cursor:pointer;`"
                                            :value="option.value">{{ option.internal_label }}
                                        </v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>
                    </div>
                    
                    <div class="d-block bordered_block my-6 py-3">
                        <div class="d-block">
                            <div class="label_medium info--text d-inline">Start Date:</div>
                            <div class="label_medium primary--text text--darken-3 d-inline ml-2">{{ formatDate(projectDetails.date_initialized) }}</div> 
                        </div>
                        <div class="d-block mt-2">
                            <div class="label_medium info--text d-inline">Expiration Date:</div>
                            <div class="label_medium primary--text text--darken-3 d-inline ml-2">{{ formatDate(projectDetails.date_expiration) }}</div> 
                        </div>
                        <div class="d-block mt-4" v-if="projectDetails.date_submitted">
                            <div class="label_medium info--text d-inline">Submitted Date:</div>
                            <div class="label_medium primary--text text--darken-3 d-inline ml-2">{{ formatDate(projectDetails.date_submitted) }}</div> 
                        </div>
                        <div class="d-block mt-2">
                            <div class="label_medium info--text d-inline">{{ projectDetails.status == 'complete' ? 'Completion Date:' : 'Estimated Completion Date:' }}</div>
                            <div v-if="!showCompletedDate">
                                <v-menu
                                    v-model="projectDetails.date_menu"
                                    :close-on-content-click="false"
                                    transition="scale-transition"
                                    offset-y
                                    class="d-flex"
                                    min-width="290px">
                                    <template v-slot:activator="{ on, attrs }">
                                        <div class="d-flex">
                                            <v-text-field
                                                v-model="formattedCloseDate"
                                                append-outer-icon="mdi-calendar"
                                                class="mt-n2 pr-5"
                                                color="primary darken-1"
                                                hide-details
                                                v-bind="attrs"
                                                v-on="on">
                                            </v-text-field>
                                            <v-btn
                                                icon
                                                class="ml-n5 clear_ecd"
                                                color="primary darken-1"
                                                @click="formattedCloseDate = null; updateECD(projectDetails)">                                        
                                                <v-icon>mdi-calendar-remove</v-icon>
                                            </v-btn>
                                        </div>
                                    </template>
                                    <v-date-picker
                                        v-model="formattedCloseDate"
                                        :disabled="projectDetails.is_archived"
                                        @input="projectDetails.date_menu = false; updateECD(projectDetails)">
                                    </v-date-picker>
                                    
                                </v-menu>
                            </div>
                            <div v-else class="label_medium primary--text text--darken-3 d-inline ml-2">
                                {{ formatDate(projectDetails.close_date) }}
                            </div>
                        </div>
                    </div>

                    <div class="d-flex d-block justify-space-between my-4 py-1">
                        <div class="d-flex justify-start d-inline">
                            <div class="d-inline label_medium info--text">Annual Project:</div>
                            <div class="d-inline ml-2">
                                <v-switch
                                    v-model="projectDetails.is_annual"
                                    color="success darken-2"
                                    class="justify-end d-inline"
                                    hide-details
                                    @click="annualArgs = projectDetails; annualProject()">
                                </v-switch>
                            </div>
                        </div>
                        <div v-if="projectDetails.is_annual"
                            class="d-inline d-flex justify-end mt-n1">
                            <div class="d-inline mt-1 mr-2 label_medium info--text">Auto Renew:</div>
                            <v-switch
                                v-model="projectDetails.auto_renew"
                                hide-details
                                color="primary darken-1"
                                class="my-0"
                                @change="updateAutoRenew(projectDetails)">
                            </v-switch>
                        </div>
                    </div>
                    
                    <div v-if="projectDetails.modinfo.expedited_days_to_complete !== null && 
                              ( !['valuation-starter', 'valuation-premium', 'valuation-elite', 'entity-maintenance-program'].includes(projectDetails.modinfo.module_key) || 
                              ( ['valuation-starter', 'valuation-premium', 'valuation-elite'].includes(projectDetails.modinfo.module_key) && projectDetails.is_annual === false ) )" 
                        class="d-flex d-block my-4 py-1">
                        <div class="label_medium info--text d-inline">Priority Access:</div>
                        <div class="label_medium primary--text text--darken-3 d-inline ml-2">
                            <v-switch
                                v-model="projectDetails.expedite"
                                color="success darken-2"
                                class="justify-end d-inline"
                                hide-details
                                @click="expediteDialog = true; expediteArgs = projectDetails">
                            </v-switch>
                        </div>
                    </div>

                    <div v-if="!projectDetails.is_archived && ( ['root'].includes(levs_cookie) || ['entity-maintenance-program'].includes(projectDetails.modinfo.module_key) )" 
                        class="d-block d-flex align-center justify-end primary--text text--darken-3 my-4">
                        <ProjectRenewButton
                            :projectDetails="projectDetails"
                            :triggerUserFeedback="triggerUserFeedback"
                            @projectRenewalCleanup="projectRenewalCleanup">
                        </ProjectRenewButton>
                        <!-- <v-btn 
                            class="white--text"
                            color="primary darken-1"
                            @click="renewProject(projectDetails)">
                            {{ projectDetails.modinfo.module_key === 'entity-maintenance-program' ? `Renew Now` : `Renew This Project` }}
                        </v-btn> -->
                    </div>

                    <div class="d-flex d-block my-6 py-1">
                        <div class="label_medium info--text d-inline">Independent Uploads:</div>
                        <div class="label_medium primary--text text--darken-3 d-inline ml-2">
                            <v-switch
                                v-model="projectDetails.upload_independent_docs"
                                color="success darken-2"
                                class="justify-end d-inline"
                                hide-details
                                @click="switchIndependentFileUploadFlag(projectDetails)">
                            </v-switch>
                        </div>
                    </div>

                    <div v-if="projectDetails.modinfo.module_key.includes('entity')" class="d-block mt-4 mb-0 py-1">
                        <div class="label_medium info--text d-inline">Effective Date</div>
                        <!-- <div v-if="!showCompletedDate"> -->
                            <v-menu
                                v-model="projectDetails.effective_date_menu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="formattedEffectiveDate"
                                        append-outer-icon="mdi-calendar"
                                        class="mt-n2 pr-5"
                                        color="primary darken-1"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on">
                                    </v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="formattedEffectiveDate"
                                    :disabled="projectDetails.is_archived"
                                    @input="projectDetails.effective_date_menu = false; updateEffectiveDate(projectDetails)">
                                </v-date-picker>
                            </v-menu>
                        <!-- </div>
                        <div v-else class="label_medium primary--text text--darken-3 d-inline ml-2">
                            {{ formatDate(projectDetails.effective_date) }}
                        </div> -->
                    </div>
                    
                    <div v-if="projectDetails.modinfo.module_key != 'entity-maintenance-program'" class="my-6 py-0 d-block d-flex align-center justify-start info--text label_medium">
                        <v-select
                            v-model="projectDetails.document_status"
                            :items="admin_document_status_list"
                            label="Document Status"
                            color="primary darken-1"
                            append-outer-icon="mdi-file-star-four-points"
                            menu-props="auto"
                            @change="updateDocumentStatus(projectDetails)">
                        </v-select> 
                    </div>

                    <!-- <div v-if="projectDetails.modinfo.non_sequential === true" 
                        class="d-block d-flex align-center justify-start info--text label_medium">
                        <div class="d-inline d-flex align-center primary--text text--darken-3">
                            <v-select
                                v-model="projectDetails.status"
                                :items="status_options"
                                item-text="label"
                                item-value="value"
                                label="Project Status"
                                class="d-inline"
                                @change="updateProjectStatus(projectDetails)">
                            </v-select> 
                            
                        </div>
                    </div> -->
                </div>
            </v-col>

            <!-- COLUMN 2 ///////////////////////////////////////// -->
            <v-col cols="4" md="4" sm="4" class="">
                <div class="d-block mb-4 info--text label_medium">
                    <div class="d-block d-flex justify-space-between">Upcoming Call Scheduled: <div v-if="projectDetails.scheduled_calls == false" class="primary--text text--darken-3">Nothing scheduled</div></div>
                    <div v-if="projectDetails.scheduled_calls" class="d-block d-flex primary--text text--darken-3 my-1 justify-space-between">
                        <div class="next-call-label">{{ projectDetails.scheduled_calls[0][projectDetails.project_id].next_call.label }}</div>
                        <div class="next-call-date">{{ formatDate( projectDetails.scheduled_calls[0][projectDetails.project_id].next_call.next_call_date ) }}</div>
                    </div>
                </div>

                <div class="d-block my-6 py-1 info--text label_medium" v-if="projectDetails.modinfo.module_key != 'entity-maintenance-program'">
                    <div class="d-block d-flex justify-space-between">Additional Calls Scheduled:<div v-if="projectDetails.scheduled_calls == false || !projectDetails.scheduled_calls[0][projectDetails.project_id].remaining.length" class="primary--text text--darken-3">Nothing scheduled</div></div>
                    <div v-if="projectDetails.scheduled_calls && projectDetails.scheduled_calls[0][projectDetails.project_id].remaining.length">
                        <div v-for="(item, index) in projectDetails.scheduled_calls[0][projectDetails.project_id].remaining" :key="index" class="d-block d-flex primary--text text--darken-3 my-1 justify-space-between">
                            <div class="meeting-label">{{ item.label }}</div>
                            <div class="meeting-date">{{ formatDate(item.date) }}</div>
                        </div>
                    </div>
                </div>

                <div class="d-block my-4 py-1 d-flex align-center justify-start info--text label_medium">
                    <v-select
                        v-model="projectDetails.calls_used"
                        :items="call_options"
                        label="Calls"
                        class="d-inline"
                        @change="updateCallCount(projectDetails)">
                    </v-select> 
                    <div class="d-inline ml-3">of <strong>{{ projectDetails.modinfo.calls_allowed }}</strong> used</div>
                </div>

                <div v-if="revision_opportunities"
                    class="d-block my-4 py-1 d-flex align-center justify-start info--text label_medium">
                        <v-select
                            v-model="projectDetails.revisions_used"
                            :items="revision_options"
                            label="Revision Rounds"
                            class="d-inline"
                            @change="updateRevisionsCount(projectDetails)">
                        </v-select> 
                        <div class="d-inline ml-3">of <strong>{{ projectDetails.modinfo.revision_rounds }}</strong> used</div>
                </div>

                <div class="d-block mt-6 justify-start info--text label_medium">
                    <v-text-field
                        v-model="projectDetails.file_server_path"
                        label="FileSrvr Path"
                        class=""
                        color="primary darken-1">
                    </v-text-field>
                    <div class="d-block d-flex justify-end">
                        <v-btn 
                            color="primary" 
                            class="d-flex d-block justify-end ml-1 mt-n3 mb-1"
                            auto-grow
                            outlined
                            x-small
                            @click="updateFilePath({project_id: projectDetails.project_id, file_server_path: projectDetails.file_server_path})">
                            save path
                        </v-btn>
                    </div>
                </div>

                <div class="d-block mt-10 justify-start info--text label_medium">
                    <WysiwygEditor
                        v-model="formattedQuickNotes"
                        :toolbar="quickNotesToolbar">
                    </WysiwygEditor>
                    <div class="d-block d-flex justify-end">
                        <v-btn 
                            color="primary" 
                            class="d-flex d-block justify-end ml-1 mt-2 mb-1"
                            auto-grow
                            outlined
                            x-small
                            @click="saveQuickNotes({project_id: projectDetails.project_id, quick_notes: projectDetails.quick_notes})">
                            save notes
                        </v-btn>
                    </div>
                </div>
            </v-col>
            
            <!-- COLUMN 3 ///////////////////////////////////////// -->
            <v-col cols="4" md="4" sm="4" class="">
                <div class="d-flex">
                    <div class="justify-start d-inline mr-6 ">
                        <div
                            v-if="projectDetails.modinfo.stages.filter((s) => !s.is_independent).length > 0"
                            class="label_medium info--text d-block">Roadmap</div>
                        <v-switch
                            v-for="(step, ndx) of projectDetails.modinfo.stages.filter((s) => !s.is_independent)"
                            :key="step.id"
                            v-model="step.completed_val"
                            hide-details
                            color="primary darken-1"
                            class="my-3 justify-start"
                            :label="step.stage_name"
                            :disabled="projectDetails.is_archived || ndx == projectDetails.modinfo.stages.findLastIndex((element) => element)"
                            @click="callSaveProject(stepArgs = {prj: projectDetails, step})">
                        </v-switch>
                    </div>
                </div>
                
                <div class="justify-end d-flex">
                    <div class="justify-start d-inline mr-6">
                        <div
                            v-if="projectDetails.modinfo.stages.filter((s) => !!s.is_independent).length > 0"
                            class="label_medium info--text d-block">Independent Steps</div>
                        <div
                            v-if="projectDetails.modinfo.stages.filter((s) => !!s.is_independent).length > 0"
                            class="label_small info--text d-block">Primary</div>
                        <v-switch
                            v-for="(step, ndx) of projectDetails.modinfo.stages.filter((s) => !!s.is_independent && !!s.is_primary)"
                            :key="step.id"
                            v-model="step.completed_val"
                            hide-details
                            color="primary darken-1"
                            class="my-3 justify-start"
                            :label="step.stage_name"
                            :disabled="projectDetails.is_archived || ndx == projectDetails.modinfo.stages.findLastIndex((element) => element)"
                            @click="callSaveProject(stepArgs = {prj: projectDetails, step})">
                        </v-switch>
                        <div
                            v-if="projectDetails.modinfo.stages.filter((s) => !!s.is_independent).length > 0"
                            class="label_small info--text d-block">Secondary
                        </div>
                        <v-switch
                            v-for="(step, ndx) of projectDetails.modinfo.stages.filter((s) => !!s.is_independent && !s.is_primary)"
                            :key="step.id"
                            v-model="step.completed_val"
                            hide-details
                            color="primary darken-1"
                            class="my-3 justify-start"
                            :label="step.stage_name"
                            :disabled="projectDetails.is_archived || ndx == projectDetails.modinfo.stages.findLastIndex((element) => element)"
                            @click="callSaveProject(stepArgs = {prj: projectDetails, step})">
                        </v-switch>
                    </div>
                </div>
            </v-col>
        </v-row>
    </div>
</template>

<script>  
    import axios from "axios"
    import Cookie from 'js-cookie'
    import appUrls from '../helpers/environment'
    import { toDollarsListings, allowedDates } from './utilities'

    import ProjectRenewButton from "./shared/projectRenewButton.vue"
    import WysiwygEditor from './WysiwygEditor.vue'
    import { quickNotesToolbar } from './shared/editorToolbars'

    export default {
        name: 'ProjectData',

        components: {
            ProjectRenewButton,
            WysiwygEditor
        },

        props: {
            projectDetails: Object,
            project_calls: Number,
            revision_opportunities: Number,
            showCompletedDate: Boolean,
            entity_filing_status_list: Array,
            admin_document_status_list: Array,
            report_type_options_list: Array,
            annual_report_filing_status_list: Array,
            project_statuses: Array,
            estCompletionDate: Function,
            pullActivityLogs: Function,
            saveProject: Function,
            holdProjectStatus: Function,
            refreshUsers: Function,
            formatThisDate: Function,
            triggerUserFeedback: Function,
            openProjectDetails: Function,
            closeProject: Function,
            thisProjectDetail: Function,
            refreshProjects: Function,
        },
        
        computed: {
            call_options () {
                let opts = [];
                for (let i = 0; i <= this.project_calls+3; i++){
                    opts.push({text:i, value:i});
                }
                return opts
            },

            revision_options () {
                let opts = [];
                for (let i = 0; i <= this.revision_opportunities+1; i++){
                    opts.push({text:i, value:i});
                }
                return opts
            },

            expedite_charge_display () {
                return toDollarsListings(this.expediteArgs.modinfo.expedited_cost)
            },

            formattedQuickNotes: {
                get() {
                    return this.projectDetails.quick_notes;
                },
                set(value) {
                    this.projectDetails.quick_notes = value;
                }
            },

            formattedCloseDate: {
                get() {
                    return this.projectDetails.close_date ? this.formatMenuDate(this.projectDetails.close_date) : ''
                },
                set(value) {
                    this.projectDetails.close_date = value
                    return this.formatMenuDate(value);
                }
            },

            formattedEffectiveDate: {
                get() {
                    return this.projectDetails.effective_date ? this.formatMenuDate(this.projectDetails.effective_date) : ''
                },
                set(value) {
                    this.projectDetails.effective_date = value
                    return this.formatMenuDate(value);
                }
            },

            // openProjectDetails: async function ( item ) {
            //     this.projectDataDialog = true
            //     this.this_userId = item.client_info[0].user_id
            //     this.this_projectId = item.project_id
            //     this.this_clientInfo = item.client_info
            // },

            // status_options () {
            //     return [
            //         { value: 'open',        label: 'Open' },
            //         { value: 'review',      label: 'Review' },
            //         { value: 'waiting',     label: 'Awaiting Data' },
            //         { value: 'processing',  label: 'Processing' },
            //         { value: 'complete',    label: 'Complete' },
            //         { value: 'hold',        label: 'Hold' },
            //         { value: 'closed',      label: 'Closed' },
            //         { value: 'upgrade',     label: 'Upgrade/Downgrade' },
            //     ];
            // },
            // progress_options () {
            //     return [
            //         { value: 'not_started',     label: 'Not Started' },
            //         { value: 'pending',         label: 'Pending' },
            //         { value: 'completed',       label: 'Completed' },
            //         { value: 'not_applicable',  label: 'Not Applicable' },
            //         { value: 'not_included',    label: 'Not Included – Client Responsibility' },
            //     ]
            // },
            // report_type_options () {
            //     return [
            //         { value: 'annual',          label: 'Annual' },
            //         { value: 'biannual_even',   label: 'Biannual (even)' },
            //         { value: 'biannual_odd',    label: 'Biannual (odd)' },
            //         { value: 'none',            label: 'None' },
            //         { value: 'tax_report',      label: 'Tax Report' },
                    
            //     ]
            // },
        },

        mounted () {
            this.getStatusData()
        },

        data () {
            return {
                user_data: {},
                date_menu: false,
                dateMenu: false,
                triggerFeedback: false,
                expediteDialog: false,
                annualArgs: {},
                stepArgs: {},
                statusArgs: {},
                expediteArgs: {},
                moduleKey: '',
                moduleId: '',
                projectId: '',
                updateProject: {},
                isHoldMenuVisible: false,
                holdMenuPosition: { x: 0, y: 0 },
                statusOptions: [],
                projectDataDialog: false,
                this_userId: '',
                this_projectId: this.projectId,
                this_clientInfo: [],
                levs_cookie: Cookie.get('levs'),
                
                quickNotesToolbar
            }
        },
        
        methods: {
            allowedDates: allowedDates,

            getStatusData: async function () {
                try {
                    const get_project_status_data = await axios.get(`${appUrls.api}/api/project_status_data`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" }
                    })

                    this.statusOptions = get_project_status_data.data.status_data

                } catch ( error ) {
                    console.log(error);
                }
            },

            formatMenuDate: function ( thisDate ) {
                if (!thisDate) return '';

                return new Date(thisDate).toISOString().split('T')[0]
            },

            formatDate: function ( date_to_format ) {
                const options = {
                    month: '2-digit', 
                    day: '2-digit', 
                    year: 'numeric'
                }

                const formatted_date = this.formatThisDate(date_to_format, options);
                return formatted_date ? formatted_date : '';
            },

            updateECD: function ( data ) {
                this.$emit('estCompletionDate', data.user_id, data.project_id, data.close_date);
                setTimeout(() => { this.$emit('pullActivityLogs', data) }, 500)
            },

            projectRenewalCleanup: function () {
                this.$emit('triggerUserFeedback', true, "Project Cloned and Archived" );
                this.$emit('closeProject', true);
                this.$emit('refreshProjects', true);
            },

            updateEffectiveDate: async function ( data ) {
                try {
                    const u_effective_date = await axios.put(`${appUrls.api}/api/admin/project_effectivedate`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        user_id: data.user_id,
                        project_id: data.project_id,
                        effective_date: data.effective_date
                    })
                
                    if ( u_effective_date.data.outcome ) {                        
                        this.$feedback.success( u_effective_date.data.message );

                        setTimeout(() => {
                            this.thisProjectDetail(u_effective_date.data.project_id, data.user_id);
                        }, 2000)
                    }
                } catch ( error ) {
                    console.log(error);
                }
            },

            switchIndependentFileUploadFlag: async function ( data ) {
                try {
                    const independent_uploads = await axios.put(`${appUrls.api}/api/admin/upload_independent_flag`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        user_id: data.user_id,
                        project_id: data.project_id,
                        upload_independent_docs: data.upload_independent_docs
                    })

                    if ( independent_uploads.data.outcome ) {                        
                        this.$feedback.success( independent_uploads.data.message );

                        setTimeout(() => {
                            this.thisProjectDetail(independent_uploads.data.project_id, data.user_id);
                        }, 2000)
                    }
                } catch ( error ) {
                    console.log(error);
                }
            },

            updateAutoRenew: async function ( data ) {
                try {
                    const updateData = {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        user_id: data.user_id,
                        project_id: data.project_id,
                        auto_renew: data.auto_renew
                    };

                    const request = await axios.put(`${appUrls.api}/api/admin/project_auto_renew`, updateData)
                
                    if ( request.data.outcome ) {
                        this.$feedback.success( request.data.message );

                        setTimeout(() => {
                            this.thisProjectDetail(request.data.project_id, data.user_id);
                        }, 2000)
                    }
                } catch ( error ) {
                    console.log(error);
                }
            },

            expediteProject: async function () {
                try {
                    const expedite_project_rs = await axios.put(`${appUrls.api}/api/admin/project_expedite`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        project: {
                            user_id: this.expediteArgs.user_id,
                            project_id: this.expediteArgs.project_id,
                            expedite: this.expediteArgs.expedite
                        }
                    })

                    if( expedite_project_rs.data.outcome ){
                        // this.updateProject = {}
                        this.$emit('triggerUserFeedback', true, expedite_project_rs.data.message);
                        this.$emit('pullActivityLogs', this.expediteArgs)
                        this.expediteDialog = false
                        this.expediteArgs = {}
                    } else {
                        this.$emit('triggerUserFeedback', true, expedite_project_rs.data.message, 'accent' );
                        this.$emit('pullActivityLogs', this.expediteArgs)
                        this.expediteDialog = false
                        this.expediteArgs = {}
                    }

                } catch ( error ) {
                    console.log(error);
                }
            },

            annualProject: async function () {
                try {
                    const annual_project_rs = await axios.put(`${appUrls.api}/api/admin/annual_project`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        project: {
                            user_id: this.annualArgs.user_id,
                            project_id: this.annualArgs.project_id,
                            is_annual: this.annualArgs.is_annual
                        }
                    })

                    if( annual_project_rs.data.outcome ){
                        this.$emit('triggerUserFeedback', true, annual_project_rs.data.message);
                        this.$emit('pullActivityLogs', this.annualArgs)
                        this.annualArgs = {}
                    } else {
                        this.$emit('triggerUserFeedback', true, annual_project_rs.data.message, 'accent' );
                        this.$emit('pullActivityLogs', this.annualArgs)
                        this.annualArgs = {}
                    }

                } catch ( error ) {
                    console.log(error);
                }
            },

            callSaveProject: async function () {
                this.$emit('saveProject', this.stepArgs)
            },

            openRelatedProject: function ( mod ) {
                if ( !mod || !mod.project_id || !mod.user_id ) return

                this.$emit('openRelatedProject', {
                    project_id: mod.project_id,
                    user_id: mod.user_id
                })
            },

            getProjectTitleTooltip: function ( mod ) {
                const projectTitle = mod && mod.project_title ? String(mod.project_title).trim() : ''
                return projectTitle || 'no title'
            },

            openStatusMenu( event ) {
                this.holdMenuPosition.x = event.clientX
                this.holdMenuPosition.y = event.clientY
                this.isHoldMenuVisible = true
            },

            changeStatus: async function ( selectedStatusOption ) {
                if ( this.projectDetails.status == selectedStatusOption ) {
                    this.isHoldMenuVisible = false
                    this.$emit('triggerUserFeedback', true, 'This project\'s status is already set to this option.', 'accent' );
                    return
                }

                this.statusOptions.find( status => {
                    if ( status.value == selectedStatusOption ) {
                        this.projectDetails.status                  = status.value
                        this.projectDetails.status_internal_label   = status.internal_label
                        this.projectDetails.status_color            = status.status_color
                        this.projectDetails.status_progress_color   = status.status_progress_color
                        this.projectDetails.status_text_color       = status.status_text_color
                    }
                })

                this.updateProjectStatus( this.projectDetails )
            },

            updateDocumentStatus: async function ( project ) {
                try {
                    const update_document_status_rs = await axios.put(`${appUrls.api}/api/admin/update_document_status`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        project: {
                            project_id: project.project_id,
                            document_status: project.document_status
                        }
                    })

                    if( update_document_status_rs.data.outcome ){
                        this.$emit('triggerUserFeedback', true, update_document_status_rs.data.message);
                    } else {
                        this.$emit('triggerUserFeedback', true, update_document_status_rs.data.message, 'accent' );
                    }

                } catch ( error ) {
                    console.log(error);
                }
            },

            updateCallCount: async function ( project ) {
                try {
                    const update_project_callcount_rs = await axios.put(`${appUrls.api}/api/admin/update_call_count`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        project: {
                            project_id: project.project_id,
                            calls_used: project.calls_used
                        }
                    })

                    if( update_project_callcount_rs.data.outcome ){
                        this.$emit('triggerUserFeedback', true, update_project_callcount_rs.data.message);
                    } else {
                        this.$emit('triggerUserFeedback', true, update_project_callcount_rs.data.message, 'accent' );
                    }

                } catch ( error ) {
                    console.log(error);
                }
            },

            updateProjectStatus: async function ( project ) {
                // console.log('update project status', project);
                try {
                    const update_project_status_rs = await axios.put(`${appUrls.api}/api/admin/update_project_status`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        project
                    })
                    this.$emit('triggerUserFeedback', true, update_project_status_rs.data.message);
                } catch ( error ) {
                    console.log(error);
                }
            },

            updateRevisionsCount: async function ( project ) {
                try {
                    const update_project_revcount_rs = await axios.put(`${appUrls.api}/api/admin/update_revisions_count`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        project: {
                            project_id: project.project_id,
                            revisions_used: project.revisions_used
                        }
                    })

                    if( update_project_revcount_rs.data.outcome ){
                        this.$emit('triggerUserFeedback', true, update_project_revcount_rs.data.message);
                    } else {
                        this.$emit('triggerUserFeedback', true, update_project_revcount_rs.data.message, 'accent' );
                    }

                } catch ( error ) {
                    console.log(error);
                }
            },

            saveQuickNotes: async function ( project ) {
                try {
                    const quick_note_rs = await axios.post(`${appUrls.api}/api/admin/save_quick_notes`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        project
                    })

                    if ( quick_note_rs.data.outcome ) {
                        this.$emit('triggerUserFeedback', true, quick_note_rs.data.message);
                    } else {
                        this.$emit('triggerUserFeedback', true, quick_note_rs.data.message, 'accent' );
                    }
                } catch ( error ) {
                    console.log(error);
                }
            },

            updateFilePath: async function ( project ) {
                try {
                    const file_server_path_rs = await axios.post(`${appUrls.api}/api/admin/file_server_path`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        project
                    })

                    if ( file_server_path_rs.data.outcome ) {
                        this.$emit('triggerUserFeedback', true, file_server_path_rs.data.message);
                    } else {
                        this.$emit('triggerUserFeedback', true, file_server_path_rs.data.message, 'accent' );
                    }
                } catch ( error ) {
                    console.log(error);
                }
            }
        }
    }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
    .bg_pewter {
        background-color: #E9EAEC !important;
    }
    .menu_bg_primary {
        background-color: #eef7f8 !important;
    }
    .icon-size {
        width: 3rem;
    }
    .icon-size-small {
        width: 2.25rem;
    }
    .icon-size-header {
        width: 3rem;
    }
    .icon-center {
        margin: 0 auto;
    }
    .refresh_response {
        font-size: .8rem;
    }
    .click_indicator {
		width: 50px; 
		height: 3px; 
		border-top: 1px dotted rgba(0,0,0,.3);
		border-bottom: 1px dotted rgba(0,0,0,.3);
	}
    .clear_ecd {
        margin-top: 2px
    }
    .user_link {
        text-decoration: none;
    }
    .user_link:hover {
        text-decoration: underline;
    }
    .multi-service-tag {
        border-radius: 5px;
        background: #e09551; 
        font-size: 11px;
        font-weight: bold;
        color: white;
        text-align: center;
        width: 146px;
        height: 24px;
    }
    .multi-project-link-chip {
        cursor: pointer;
    }
    .bordered_block { 
        /* rounded corners, with a background color and padding to separate it from other sections */
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
        border: 1px solid #ccc;
        background-color: #fcfcfc;
        padding: 8px;
    }


    .customToolbar {
        border: 1px solid #ccc;
        padding: 10px;
        margin-top: 10px;
        background-color: #f9f9f9;
    }

    .customToolbar img {
        max-width: 100%;
        height: auto;
    }
    .customToolbar a {
        color: #007bff;
        text-decoration: none;
    }
    .customToolbar a:hover {
        text-decoration: underline;
    }
    .customToolbar p {
        margin: 0;
        padding: 0;
    }
    .customToolbar ul {
        list-style-type: disc;
        padding-left: 20px;
    }
    .customToolbar li {
        margin-bottom: 5px;
    }
    .customToolbar h1, .customToolbar h2, .customToolbar h3 {
        margin: 0;
        padding: 0;
    }
    .customToolbar h1 {
        font-size: 24px;
    }
    .customToolbar h2 {
        font-size: 20px;
    }
    .customToolbar h3 {
        font-size: 18px;
    }
    .customToolbar blockquote {
        border-left: 4px solid #ccc;
        padding-left: 10px;
        margin: 0;
        font-style: italic;
    }
    .customToolbar pre {
        background-color: #f0f0f0;
        padding: 10px;
        overflow-x: auto;
    }
</style>


// WEBPACK FOOTER //
// src/components/ProjectData.vue